import { useState } from "react";

import { SetupMfaByTotpPayload } from "auth/AuthContext";
import { Button } from "components/Button/Button";
import { ClickableIcon } from "components/Icon/ClickableIcon";
import { MfaMethodKindKnownValue } from "generated/account";
import { useTranslation } from "i18n";
import { run } from "utils";

import { MfaCodeForm } from "./MfaCodeForm";

export type SetupMfaFormProps = {
  organizationName: string;
  onGoBack?: () => void;
  onSubmit: (
    method: MfaMethodKindKnownValue,
    code: string,
  ) => Promise<"SUCCESS" | "ERROR">;
} & SetupMfaByTotpPayload;

type Step = "TOTP_QR_CODE" | "TOTP_CONFIRMATION";

export const SetupMfaForm = ({
  organizationName,
  onGoBack,
  onSubmit,
  tentativeQrCode,
}: SetupMfaFormProps) => {
  const initialStep = "TOTP_QR_CODE";
  const [step, setStep] = useState<Step>(initialStep);

  return (
    <div className="flex-col">
      {(step !== initialStep || onGoBack) && (
        <ClickableIcon
          name="chevron"
          rotate={180}
          className="mb-24 border rounded-full h-44 w-44"
          onClick={() => {
            switch (step) {
              case "TOTP_QR_CODE":
                setStep(initialStep);
                break;
              case "TOTP_CONFIRMATION":
                setStep("TOTP_QR_CODE");
                break;
            }
          }}
        />
      )}

      {run(() => {
        switch (step) {
          case "TOTP_QR_CODE":
            return (
              <SetupMfaByTotpQrCode
                organizationName={organizationName}
                onContinue={() => setStep("TOTP_CONFIRMATION")}
                tentativeQrCode={tentativeQrCode}
              />
            );
          case "TOTP_CONFIRMATION":
            return (
              <SetupMfaByTotpConfirmationForm
                onSubmit={(code) => onSubmit("TOTP", code)}
              />
            );
        }
      })}
    </div>
  );
};

const SetupMfaByTotpQrCode = ({
  organizationName,
  tentativeQrCode,
  onContinue,
}: SetupMfaByTotpPayload & {
  organizationName: string;
  onContinue: () => void;
}) => {
  const t = useTranslation();

  return (
    <>
      <h2 className="text-black font-medium mb-4">
        {t("setup_mfa_form.title")}
      </h2>
      <p className="leading-normal mb-18">
        {t("setup_mfa_form.totp_qr_code_subtitle", { organizationName })}
      </p>

      <img
        className="w-[300px] h-[300px] self-center"
        style={{ imageRendering: "pixelated" }}
        src={`data:image/jpeg;base64,${tentativeQrCode}`}
        alt="QR code"
      />

      <Button
        large
        label={t("setup_mfa_form.continue")}
        onClick={onContinue}
        className="mt-28"
      />
    </>
  );
};

const SetupMfaByTotpConfirmationForm = ({
  onSubmit,
}: {
  onSubmit: (code: string) => Promise<"SUCCESS" | "ERROR">;
}) => {
  const t = useTranslation();
  return (
    <MfaCodeForm
      subtitle={t("setup_mfa_form.totp_confirm_subtitle")}
      onSubmit={onSubmit}
    />
  );
};
